import React, { useState } from 'react';
import styles from './Transactions.module.css';

function fmt(n) { return `$${Math.abs(Number(n)).toFixed(2)}`; }

function fmtDateTime(d) {
  if (!d) return '—';
  const s = typeof d === 'string' ? d : d.toISOString();
  // Plain date string (YYYY-MM-DD) — use noon to avoid timezone rollback
  if (s.length === 10) return new Date(s + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const date = new Date(s);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    + ' · ' + date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

const SOURCE_META = {
  square:  { label: 'Square',  bg: '#e6f4ea', fg: '#1e7d40' },
  venmo:   { label: 'Venmo',   bg: '#e8f1fb', fg: '#2074c0' },
  account: { label: 'Account', bg: '#f1ecfb', fg: '#6b3dbf' },
};
function SourceBadge({ source }) {
  if (!source) return <span style={{ color: '#999', fontSize: '0.78rem' }}>—</span>;
  const meta = SOURCE_META[source] || { label: source, bg: '#eee', fg: '#555' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 10,
      background: meta.bg, color: meta.fg, fontSize: '0.72rem', fontWeight: 700,
      letterSpacing: '0.04em', textTransform: 'uppercase',
    }}>{meta.label}</span>
  );
}

function extractAthlete(note) {
  if (!note) return null;
  const m = note.match(/\u2014\s*([^|]+)/);
  if (!m) return null;
  return m[1].replace(/\s*\(.*$/, '').trim() || null;
}

export default function Transactions({ transactions, clients, config }) {
  const [search, setSearch]       = useState('');
  const [typeFilter, setType]     = useState('all');
  const [clientFilter, setClient] = useState('all');

  const sessionLabel = (type) =>
    config?.sessionRates?.[type]?.label || type;

  const filtered = transactions
    .filter(t => {
      if (typeFilter === 'sessions' && t.type !== 'session_debit') return false;
      if (typeFilter === 'credits'  && t.type !== 'credit_load')   return false;
      if (clientFilter !== 'all' && t.clientId !== clientFilter)   return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          (t.clientName || '').toLowerCase().includes(q) ||
          (t.note || '').toLowerCase().includes(q)
        );
      }
      return true;
    })
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const totalDebits  = filtered.filter(t => t.type === 'session_debit').reduce((s, t) => s + Math.abs(parseFloat(t.amount || 0)), 0);
  const totalCredits = filtered.filter(t => t.type === 'credit_load').reduce((s, t) => s + parseFloat(t.amount || 0), 0);

  return (
    <div className={styles.page}>

      {/* Filters */}
      <div className={styles.filtersRow}>
        <input
          className={styles.search}
          placeholder="Search client or description..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select className={styles.select} value={typeFilter} onChange={e => setType(e.target.value)}>
          <option value="all">All Types</option>
          <option value="sessions">Sessions Only</option>
          <option value="credits">Credit Loads Only</option>
        </select>
        <select className={styles.select} value={clientFilter} onChange={e => setClient(e.target.value)}>
          <option value="all">All Clients</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {/* Summary strip */}
      <div className={styles.summaryStrip}>
        <span className={styles.sumItem}><strong>{filtered.length}</strong>&nbsp;transactions</span>
        <span className={styles.sumDivider} />
        <span className={styles.sumItem}><strong className={styles.red}>-${totalDebits.toFixed(2)}</strong>&nbsp;sessions charged</span>
        <span className={styles.sumDivider} />
        <span className={styles.sumItem}><strong className={styles.green}>+${totalCredits.toFixed(2)}</strong>&nbsp;credits loaded</span>
      </div>

      {/* ── Desktop: table ───────────────────────────────────────────────── */}
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Date</th>
              <th>Client</th>
              <th>Type</th>
              <th>Source</th>
              <th>Description</th>
              <th>Amount</th>
              <th>Balance After</th>
              <th>Invoice ID</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={8} className={styles.emptyRow}>No transactions match your filters.</td></tr>
            )}
            {filtered.map(tx => {
              const isSession = tx.type === 'session_debit';
              const athleteName = isSession ? extractAthlete(tx.note) : null;
              return (
                <tr key={tx.id}>
                  <td className={styles.dateCell}>{fmtDateTime(tx.sessionDate || tx.createdAt)}</td>
                  <td className={styles.clientCell}>
                    <div>{tx.clientName}</div>
                    {athleteName && <div className={styles.athleteSubCell}>{athleteName}</div>}
                  </td>
                  <td>
                    {tx.type === 'credit_load'
                      ? <span className="tag tag-green">Credit Load</span>
                      : <span className="tag tag-orange">{sessionLabel(tx.sessionType)}</span>}
                  </td>
                  <td><SourceBadge source={tx.source} /></td>
                  <td className={styles.noteCell}>{tx.note}</td>
                  <td className={`${styles.amountCell} ${tx.amount > 0 ? styles.pos : styles.neg}`}>
                    {tx.amount > 0 ? '+' : '-'}{fmt(tx.amount)}
                  </td>
                  <td className={styles.balCell}>${Number(tx.balanceAfter).toFixed(2)}</td>
                  <td className={styles.invoiceCell}>{tx.squareInvoiceId || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Mobile: card list ────────────────────────────────────────────── */}
      <div className={styles.cardList}>
        {filtered.length === 0 && (
          <p className={styles.empty}>No transactions match your filters.</p>
        )}
        {filtered.map(tx => {
          const isSession = tx.type === 'session_debit';
          const athleteName = isSession ? extractAthlete(tx.note) : null;
          const isCredit = tx.type === 'credit_load';
          return (
            <div key={tx.id} className={styles.txCard}>
              <div className={styles.txCardTop}>
                <div className={styles.txCardLeft}>
                  <div className={styles.txCardClient}>{tx.clientName}</div>
                  {athleteName && <div className={styles.txCardAthlete}>{athleteName}</div>}
                </div>
                <div className={`${styles.txCardAmount} ${isCredit ? styles.pos : styles.neg}`}>
                  {isCredit ? '+' : '-'}{fmt(tx.amount)}
                </div>
              </div>
              <div className={styles.txCardMid} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                {isCredit
                  ? <span className="tag tag-green">Credit Load</span>
                  : <span className="tag tag-orange">{sessionLabel(tx.sessionType)}</span>}
                <SourceBadge source={tx.source} />
              </div>
              {tx.note && <div className={styles.txCardNote}>{tx.note}</div>}
              <div className={styles.txCardDate}>{fmtDateTime(tx.sessionDate || tx.createdAt)}</div>
            </div>
          );
        })}
      </div>

    </div>
  );
}
